<template>
	<div>
		<div class="tw-text-3xl tw-font-bold" v-if="!isDone">
			{{ $t('integrations.enter-woocommerce-details-title') }}
		</div>
		<div class="tw-flex tw-items-center tw-gap-2" v-else>
			<div class="tw-text-3xl tw-font-bold">
				{{ $t('integrations.thanks-for-submit-details') }}
			</div>
			<div
				class="'tw-border-secondary-500 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-bg-secondary-500 tw-text-white"
				style="width: 32px; height: 32px"
			>
				<AppIcon name="check" />
			</div>
		</div>
		<div class="tw-font-medium tw-text-gray-500" v-if="!isDone">
			{{ $t('integrations.enter-woocommerce-details-description') }}
		</div>
		<div class="tw-font-medium tw-text-gray-500" v-else>
			{{ $t('integrations.thanks-for-submit-woocommerce-details-description') }}
		</div>

		<!-- Stated up front because the backend rejects these outright: a non-HTTPS or
		     non-public store URL is refused before any call is made, and WooCommerce's
		     own API needs pretty permalinks. Failing here is otherwise a blank error. -->
		<div
			v-if="!isDone"
			class="tw-mt-4 tw-rounded-lg tw-border tw-border-secondary-200 tw-bg-secondary-50 tw-p-4"
			style="width: 70%"
		>
			<div class="tw-font-semibold tw-text-gray-800">
				{{ $t('integrations.woocommerce-requirements-title') }}
			</div>
			<ul class="tw-mt-2 tw-flex tw-flex-col tw-gap-1 tw-ps-5" style="list-style: disc">
				<li class="tw-text-sm tw-text-gray-600">
					{{ $t('integrations.woocommerce-requirement-https') }}
				</li>
				<li class="tw-text-sm tw-text-gray-600">
					{{ $t('integrations.woocommerce-requirement-permalinks') }}
				</li>
				<li class="tw-text-sm tw-text-gray-600">
					{{ $t('integrations.woocommerce-requirement-version') }}
				</li>
				<li class="tw-text-sm tw-text-gray-600">
					{{ $t('integrations.woocommerce-requirement-admin-user') }}
				</li>
			</ul>
			<div
				class="tw-mt-3 tw-cursor-pointer tw-text-sm tw-font-medium tw-text-blue-500 tw-underline"
				@click="openApiKeysHelp"
			>
				{{ $t('integrations.how-to-find-woocommerce-api-keys') }}
			</div>
		</div>

		<AppForm
			v-if="!isDone"
			class="tw-mt-3 tw-flex tw-flex-col tw-gap-4"
			style="width: 70%"
			@submit="onSubmit"
		>
			<div>
				<AppInput
					v-model="form.store_name"
					:label="$t('integrations.store-name')"
					:rules="['required']"
					:disabled="loading"
					:placeholder="$t('integrations.enter-store-name')"
				/>

				<AppInput
					v-model="form.store_url"
					:label="$t('integrations.store-url')"
					:rules="['required']"
					:disabled="loading"
					:placeholder="$t('integrations.enter-store-url')"
				>
					<template #hint>
						<div class="tw-text-gray-600">
							{{ $t('integrations.woocommerce-store-url-hint') }}
						</div>
					</template>
				</AppInput>

				<AppInput
					v-model="form.consumer_key"
					:label="$t('integrations.consumer-key')"
					:rules="['required']"
					:disabled="loading"
					placeholder="ck_xxxxxxxxxxxxxxxxxxxx"
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openApiKeysHelp"
						>
							{{ $t('integrations.how-to-find-woocommerce-api-keys') }}
						</div>
					</template>
				</AppInput>

				<AppInput
					v-model="form.consumer_secret"
					:label="$t('integrations.consumer-secret')"
					:rules="['required']"
					:disabled="loading"
					type="password"
					placeholder="cs_xxxxxxxxxxxxxxxxxxxx"
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openApiKeysHelp"
						>
							{{ $t('integrations.how-to-find-woocommerce-api-keys') }}
						</div>
					</template>
				</AppInput>

				<AppCheckbox
					v-model="form.auto_integrate"
					class="tw-mt-2"
					:trueValue="1"
					:falseValue="0"
				>
					<template v-slot:label="{ toggle }">
						<div class="tw-flex tw-cursor-pointer tw-flex-col tw-ps-3" @click="toggle()">
							<div class="tw-text-lg tw-font-medium tw-text-gray-700">
								{{ $t('integrations.activate-auto-sync-products-orders') }}
							</div>
							<div class="tw-text-lg tw-text-gray-600">
								{{
									$t('integrations.select-to-sync-marketplace', {
										marketplace: marketplaceName,
									})
								}}
							</div>
						</div>
					</template>
				</AppCheckbox>

				<AppCheckbox
					v-model="form.allow_product_updates"
					class="tw-mt-2"
					:trueValue="1"
					:falseValue="0"
				>
					<template v-slot:label="{ toggle }">
						<div class="tw-flex tw-cursor-pointer tw-flex-col tw-ps-3" @click="toggle()">
							<div class="tw-text-lg tw-font-medium tw-text-gray-700">
								{{ $t('integrations.allow-product-updates') }}
							</div>
							<div class="tw-text-lg tw-text-gray-600">
								{{ $t('integrations.allow-product-updates-description') }}
							</div>
						</div>
					</template>
				</AppCheckbox>
			</div>

			<div class="tw-mt-1">
				<AppButton
					:label="loading ? $t('integrations.authorizing') : $t('integrations.authorize')"
					size="xl"
					class="tw-rounded-full"
					type="submit"
					style="width: 250px"
					:disabled="loading"
				/>
			</div>
		</AppForm>
		<div class="tw-mt-3 tw-flex tw-items-center tw-gap-3" v-else>
			<AppButton
				:label="$t('integrations.next-step')"
				size="xl"
				class="tw-rounded-full"
				type="button"
				endIcon="assets/v2/image/icons/arrow-narrow-right.svg"
				@click="nextStep"
				:loading="nextStepLoading"
			/>
		</div>

		<HelpDialogApiKeys ref="helpDialogApiKeysRef" />
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useLinkMarketplaceStore } from '../../../../stores/link-marketplace';
import { Marketplace } from '../../../../entities/marketplace';
import { marketplaceStepStatusEnum } from '../../helper';
import HelpDialogApiKeys from './help-dialog-api-keys.vue';

const linkMarketplaceStore = useLinkMarketplaceStore();

const helpDialogApiKeysRef = ref(null);

const openApiKeysHelp = () => helpDialogApiKeysRef.value?.open();

const form = ref({
	store_name: '',
	store_url: '',
	consumer_key: '',
	consumer_secret: '',
	auto_integrate: 1,
	allow_product_updates: 0,
});

const loading = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.InProgress,
);
const isDone = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Complete,
);
const nextStepLoading = ref(false);

const marketplaceName = computed(() => {
	return linkMarketplaceStore.marketplace.name || '';
});

const onSubmit = async () => {
	loading.value = true;
	try {
		const success = await Marketplace.setWooCommerceDetails(form.value, linkMarketplaceStore.linkedMarketplaceId);
		if (!success) {
			document.location.href = '/store/subscribe/renew_package';
			return;
		}
		await linkMarketplaceStore.refetchSteps();
		isDone.value = true;
	} catch (err) {
		loading.value = false;
	}
};

const nextStep = async () => {
	nextStepLoading.value = true;
	try {
		await Marketplace.finishWizardStep(linkMarketplaceStore.currentStepData.id);
		await linkMarketplaceStore.refetchSteps();
	} finally {
		nextStepLoading.value = false;
	}
};
</script>

<style lang="scss" scoped></style>
